import { useSortable } from '@dnd-kit/sortable';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import React from 'react';

import { SortableHeaderCellProps } from './SortableHeaderCell.props';
import { ColumnResizeHandle } from '../../atoms/ColumnResizeHandle';

const HEADER_SX = {
  fontSize: '10px',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  fontWeight: 600,
  color: 'text.secondary',
  userSelect: 'none' as const,
  whiteSpace: 'nowrap' as const,
  py: 0.75,
  px: 1,
  borderBottom: '2px solid',
  borderColor: 'v4.border',
} as const;

export const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({
  col,
  sortModel,
  onSort,
  dropIndicator,
  onColumnResize,
}) => {
  const theme = useTheme();
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id: col.key });
  const isSorted = sortModel.field === col.key;

  const ariaSortValue = isSorted
    ? sortModel.direction === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';

  return (
    <TableCell
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      aria-sort={ariaSortValue}
      data-col-key={col.key}
      sx={{
        ...HEADER_SX,
        position: 'relative',
        cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.4 : 1,
        color: isSorted ? theme.palette.v4.accent : 'text.secondary',
        overflow: 'hidden',
        '&:hover .col-resize-handle, &:focus-within .col-resize-handle': {
          opacity: 1,
        },
        ...(dropIndicator === 'left' && {
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 4,
            bottom: 4,
            width: '2px',
            bgcolor: theme.palette.v4.accent,
            borderRadius: '1px',
          },
        }),
        ...(dropIndicator === 'right' && {
          '&::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: 4,
            bottom: 4,
            width: '2px',
            bgcolor: theme.palette.v4.accent,
            borderRadius: '1px',
          },
        }),
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => onSort(col.key)}
        aria-label={`Sort by ${col.label}, ${ariaSortValue}`}
        title={col.label}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          maxWidth: '100%',
          minWidth: 0,
          overflow: 'hidden',
          background: 'none',
          border: 'none',
          padding: 0,
          font: 'inherit',
          color: 'inherit',
          cursor: 'pointer',
          letterSpacing: 'inherit',
          textTransform: 'inherit',
          fontWeight: 'inherit',
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {col.headerLabel ?? col.label}
        </Box>
        {isSorted &&
          (sortModel.direction === 'asc' ? (
            <ArrowUpwardIcon sx={{ fontSize: 14, flexShrink: 0 }} />
          ) : (
            <ArrowDownwardIcon sx={{ fontSize: 14, flexShrink: 0 }} />
          ))}
      </Box>
      {onColumnResize && (
        <ColumnResizeHandle columnKey={col.key} label={col.label} onResize={onColumnResize} />
      )}
    </TableCell>
  );
};
